import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { hardSignout, signin, signout } from './auth';

const initialState = {
  lastAuthTime: null,
  showModal: false,
  iframeReady: false,
  timeoutStarted: false,
  // True once the lazy main App has mounted its first paint after the initial login. The pre-
  // auth shell keeps a loading cover up until then so the login screen flows straight into the
  // app with no blank gap during App's mount. Stays true across periodic reauth; reset on logout.
  appReady: false,
};

const dismissReauthPrompt = (state) => {
  state.showModal = false;
  state.iframeReady = false;
  state.timeoutStarted = false;
};

const updateAuthTimestamp = (state, time = Date.now()) => {
  state.lastAuthTime = time;
  dismissReauthPrompt(state);
};

export const reauthSlice = createSlice({
  name: 'reauth',
  initialState,
  reducers: {
    setLastAuthTime: (state, action) => {
      updateAuthTimestamp(state, action.payload);
    },
    reauthSuccess: (state) => {
      updateAuthTimestamp(state);
    },
    showReauthModal: (state) => {
      // Don't reset iframeReady if modal is already shown
      if (!state.showModal) {
        state.iframeReady = false;
      }
      state.showModal = true;
      state.timeoutStarted = true;
    },
    setIframeReady: (state) => {
      state.iframeReady = true;
    },
    setAppReady: (state) => {
      state.appReady = true;
    },
    hideReauthModal: (state) => {
      dismissReauthPrompt(state);
    },
    resetReauth: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(signin.fulfilled, (state, { payload }) => {
        if (!payload.error) {
          updateAuthTimestamp(state);
        }
      })
      .addMatcher(isAnyOf(signout.pending, hardSignout.pending), () => initialState);
  },
});

export const {
  setLastAuthTime,
  showReauthModal,
  setIframeReady,
  setAppReady,
  hideReauthModal,
  reauthSuccess,
  resetReauth,
} = reauthSlice.actions;

export const selectLastAuthTime = (state) => state.reauth.lastAuthTime;
export const selectShowReauthModal = (state) => state.reauth.showModal;
export const selectIframeReady = (state) => state.reauth.iframeReady;
export const selectAppReady = (state) => state.reauth.appReady;

export default reauthSlice.reducer;
